import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield } from 'lucide-react';

const PrivacyPage = () => {
  const sections = [
    {
      num: "01",
      title: "Who We Are",
      content: [
        "Snigdha360 (\"we\", \"our\", \"us\") is a professional compliance and tax services firm operating across the United States and India. We provide business formation, tax filing, regulatory compliance, and data governance services to individual and business clients globally.",
        "This Privacy Policy describes how Snigdha360 collects, uses, stores, and protects personal and business information in connection with our services and our website.",
        "We take data privacy seriously — not as a legal obligation to satisfy minimally, but as a professional commitment that reflects who we are. As an ISO 27001-aligned organization, our data practices are documented, auditable, and consistently applied across every engagement."
      ]
    },
    {
      num: "02",
      title: "Information We Collect",
      content: [
        "We collect information in two ways: directly from you as part of our service engagement, and automatically when you interact with our website.",
        "Information you provide directly:"
      ],
      list: [
        "Identity information: full legal name, date of birth, government-issued ID details",
        "Contact information: email address, phone number, mailing address",
        "Business information: entity name, business address, ownership structure, EIN/PAN/other tax IDs",
        "Financial information: income data, bank account details where required for tax filing purposes",
        "Tax and compliance documents: returns, notices, prior filings, financial statements",
        "Formation documents: articles of incorporation, operating agreements, shareholder registers",
        "Communication records: emails, messages, and correspondence with our team"
      ],
      content2: ["Information collected automatically:"],
      list2: [
        "Website usage data: pages visited, time on site, referring URL",
        "Device information: IP address, browser type, operating system",
        "Cookies and similar technologies (see Section 11)"
      ],
      callout: "We collect only the information necessary to deliver our services. We do not collect sensitive personal information beyond what is strictly required for tax and compliance purposes, and we never collect information for marketing resale."
    },
    {
      num: "03",
      title: "How We Use Your Information",
      content: [
        "Information collected is used exclusively for the following purposes:"
      ],
      list: [
        "Service delivery: Preparing and filing tax returns, formation documents, regulatory filings, and related services",
        "Client communication: Responding to your queries, providing updates on engagements, and sending compliance deadline reminders",
        "Identity verification: Confirming your identity and authority as required by applicable law",
        "Legal and regulatory compliance: Meeting our obligations under IRS regulations, Indian tax law, anti-money laundering requirements",
        "Service improvement: Analysing aggregated, anonymised data to improve our processes — never using identifiable data",
        "Billing and fee management: Processing payments and maintaining accurate financial records"
      ],
      footer: "We do not use your information for targeted advertising, profiling, or sale to third parties. Ever."
    },
    {
      num: "04",
      title: "Legal Basis for Processing",
      jurisdictions: ["United States", "India"],
      content: [
        "Our processing of your personal information is grounded in the following legal bases, depending on the nature of the data and the jurisdiction:"
      ],
      list: [
        "Contractual necessity: Processing required to deliver the services you have engaged us for",
        "Legal obligation: Processing required to comply with IRS regulations, Indian Income Tax Act, GST law, anti-money laundering regulations",
        "Legitimate interests: Processing necessary for our legitimate business interests, where these interests do not override your privacy rights",
        "Consent: Where we rely on consent — for example, for certain marketing communications — you may withdraw that consent at any time"
      ]
    },
    {
      num: "05",
      title: "Data Sharing & Disclosure",
      content: [
        "We do not sell, rent, or trade your information. We share information only in the following circumstances:"
      ],
      list: [
        "Government and regulatory authorities: As required by law — for example, submitting tax returns to the IRS or Indian tax authorities on your behalf",
        "Service sub-processors: Vetted third-party providers who assist us in delivering our services, operating under equivalent confidentiality obligations",
        "Professional referrals: With your explicit consent, in cases where we recommend legal counsel or other professionals",
        "Legal proceedings: Where required by a valid court order or applicable law — we will notify you where legally permitted",
        "Business transfers: In the event of a merger or acquisition, you will be notified and given the opportunity to withdraw consent"
      ],
      footer: "All sub-processors used by Snigdha360 are contractually bound to process data only for the purposes we specify and to apply security standards consistent with our own."
    },
    {
      num: "06",
      title: "Data Security",
      content: [
        "Snigdha360 is an ISO 27001-aligned organization. Our security posture is built on documented, auditable processes. We apply the following controls to protect your data:"
      ],
      list: [
        "Encryption of data in transit and at rest using industry-standard protocols",
        "Role-based access controls limiting data access to personnel with a genuine operational need",
        "Platform-level controls preventing unauthorized download or extraction of client documents",
        "Mandatory deletion of locally accessed files within 7 days, enforced at the system level",
        "Regular security reviews and process audits",
        "Confidentiality agreements binding all employees and contractors with access to client data",
        "Incident response procedures for detecting, containing, and reporting data breaches"
      ],
      callout: "Unlike most small professional services firms, Snigdha360's security controls are system-enforced, not just policy-stated. Our platform architecture prevents unauthorized file access at a technical level — because policies without enforcement are not security.",
      footer: "While we apply rigorous controls, no system is entirely without risk. In the event of a data breach affecting your information, we will notify you in accordance with applicable breach notification laws."
    },
    {
      num: "07",
      title: "Document Governance",
      content: [
        "Client documents entrusted to Snigdha360 are subject to our formal document governance framework. This framework governs how documents are classified, stored, accessed, shared, and destroyed — and is consistent with ISO 27001 principles."
      ],
      list: [
        "All client documents are classified by sensitivity and handled accordingly",
        "Access to client documents is logged and auditable",
        "No client document may be downloaded to a personal device without explicit authorization, and any such download must be permanently deleted within 7 days",
        "Documents shared with government authorities are transmitted via secure, approved channels",
        "Upon completion or termination of an engagement, documents are retained for the legally required period and then securely destroyed"
      ],
      footer: "You may request a copy of any document we hold on your behalf at any time during the active retention period."
    },
    {
      num: "08",
      title: "Data Retention",
      content: [
        "We retain your information only for as long as necessary to fulfill the purposes for which it was collected and to meet our legal and regulatory obligations."
      ],
      list: [
        "Tax records (US): Retained for a minimum of 7 years in accordance with IRS requirements",
        "Tax records (India): Retained for the period required under the Income Tax Act and GST law (typically 6–8 years)",
        "Formation and corporate documents: Retained for the life of the entity and a minimum of 7 years post-dissolution",
        "General client correspondence: Retained for 5 years from the end of the engagement",
        "Website usage data: Retained for up to 12 months"
      ],
      footer: "Following the applicable retention period, data is securely and permanently destroyed. We do not retain data beyond what is legally or operationally necessary."
    },
    {
      num: "09",
      title: "Your Privacy Rights (United States)",
      jurisdictions: ["United States"],
      content: [
        "Depending on the US state in which you reside, you may have the following rights regarding your personal information:"
      ],
      rights: [
        { title: "Right to Know", desc: "Request disclosure of the personal information we hold about you and how it is used." },
        { title: "Right to Delete", desc: "Request deletion of your personal information, subject to legal retention obligations." },
        { title: "Right to Correct", desc: "Request correction of inaccurate personal information we hold about you." },
        { title: "Right to Opt-Out", desc: "Opt out of the sale or sharing of personal information. We do not sell data — this right is automatically honoured." }
      ],
      footer: "To exercise any of these rights, contact us at legal@snigdha360.com. We will not discriminate against you for exercising these rights. For California residents: Our practices are consistent with the California Consumer Privacy Act (CCPA) as amended by the CPRA."
    },
    {
      num: "10",
      title: "Your Privacy Rights (India)",
      jurisdictions: ["India"],
      content: [
        "For clients whose data is processed in connection with services governed by Indian law, your rights are governed by the Digital Personal Data Protection Act, 2023 (DPDPA)."
      ],
      rights: [
        { title: "Right to Information", desc: "Access a summary of your personal data processed by us and the purposes for which it is processed." },
        { title: "Right to Correction", desc: "Request correction, completion, or updating of inaccurate or outdated personal data." },
        { title: "Right to Erasure", desc: "Request erasure of personal data no longer necessary for the purpose for which it was collected." },
        { title: "Right to Grievance Redressal", desc: "Lodge a grievance with our Data Protection Officer and receive a response within applicable timelines." }
      ],
      footer: "To exercise your rights, contact our Data Protection Officer at legal@snigdha360.com. You also have the right to lodge a complaint with the Data Protection Board of India."
    },
    {
      num: "11",
      title: "Cookies & Website Data",
      content: [
        "Our website uses cookies and similar technologies to ensure the site functions correctly and to understand how it is used. We use the following types of cookies:"
      ],
      list: [
        "Essential cookies: Required for basic website functionality; cannot be disabled",
        "Analytics cookies: Used to understand how visitors interact with our website using anonymised, aggregated data",
        "Preference cookies: Store your settings and preferences to improve your experience on return visits"
      ],
      footer: "We do not use advertising or tracking cookies. You can manage cookie preferences through your browser settings."
    },
    {
      num: "12",
      title: "Cross-Border Data Transfers",
      jurisdictions: ["United States", "India"],
      content: [
        "As a firm operating across the United States and India, data may be transferred between jurisdictions in the course of delivering our services. For example, a US-formed entity with India-resident directors may involve processing in both jurisdictions.",
        "Where data is transferred internationally, we ensure that appropriate safeguards are in place, including contractual protections and compliance with applicable data transfer regulations."
      ]
    },
    {
      num: "13",
      title: "Children's Privacy",
      content: [
        "Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected data from a minor, please contact us immediately and we will promptly delete such information."
      ]
    },
    {
      num: "14",
      title: "Updates to This Policy",
      content: [
        "We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or regulatory environment. We will notify active clients of material changes via email with a minimum of 14 days' notice before the revised Policy takes effect.",
        "The most current version of this Privacy Policy is always available at snigdha360.com/privacy."
      ]
    },
    {
      num: "15",
      title: "Contact & Complaints",
      content: [
        "For any questions, concerns, or requests relating to this Privacy Policy or your personal data, please contact us:"
      ],
      list: [
        "Data Privacy Enquiries: legal@snigdha360.com",
        "General Contact: snigdha360.com"
      ],
      callout: "We welcome feedback. Our formal complaint and feedback process is part of our documented service framework. Every complaint is logged, tracked, and resolved according to a written procedure — because accountability is not optional in the business of trust.",
      footer: "We aim to respond to all privacy-related requests within 10 business days. If you are not satisfied with our response, you have the right to lodge a complaint with the relevant supervisory authority in your jurisdiction."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      {/* Navigation - Same as Landing Page */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Snigdha & Associates</p>
                <p className="text-xs text-slate-500">Chartered Accountants</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/#services" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">India</Link>
              <Link to="/#us-services" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">US</Link>
              <Link to="/#contact" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">Contact</Link>
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 h-auto">
                  Client Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">Legal Documentation</p>
          <h1 className="text-4xl md:text-5xl font-light mb-4 text-slate-900">
            Privacy <span className="text-slate-400">Policy</span>
          </h1>
          <p className="text-slate-500 max-w-2xl mb-8">
            Your data is your asset. This policy explains precisely how we collect, use, protect, and respect it — in plain language, without ambiguity.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Effective Date</p>
              <p className="text-sm text-slate-900">January 1, 2025</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Last Updated</p>
              <p className="text-sm text-slate-900">January 1, 2025</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Jurisdictions</p>
              <p className="text-sm text-slate-900">United States & India</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Standard</p>
              <p className="text-sm text-slate-900">ISO 27001-Aligned</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-16">
          {sections.map((section, i) => (
            <div key={i} className="relative">
              <div className="flex items-start gap-6">
                <span className="text-4xl font-light text-slate-200 flex-shrink-0 w-16">{section.num}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <h2 className="text-xl font-medium text-slate-900">{section.title}</h2>
                    {section.jurisdictions?.map((j, idx) => (
                      <span key={idx} className={`text-xs px-2 py-0.5 rounded ${
                        j === 'United States' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
                      }`}>
                        {j === 'United States' ? 'US' : 'India'}
                      </span>
                    ))}
                  </div>
                  
                  {section.content?.map((para, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4">{para}</p>
                  ))}
                  
                  {section.list && (
                    <ul className="space-y-2 my-4">
                      {section.list.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-500 text-sm">
                          <span className="text-slate-300 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.content2?.map((para, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4 mt-6">{para}</p>
                  ))}
                  
                  {section.list2 && (
                    <ul className="space-y-2 my-4">
                      {section.list2.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-500 text-sm">
                          <span className="text-slate-300 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.rights && (
                    <div className="grid md:grid-cols-2 gap-4 my-6">
                      {section.rights.map((right, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                          <h3 className="text-sm font-medium text-slate-900 mb-2">{right.title}</h3>
                          <p className="text-xs text-slate-500">{right.desc}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {section.callout && (
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 my-4">
                      <p className="text-sm text-slate-600">{section.callout}</p>
                    </div>
                  )}
                  
                  {section.footer && (
                    <p className="text-slate-500 text-sm mt-4">{section.footer}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer - Same as Landing Page */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-8 h-8 object-contain"
              />
              <div>
                <p className="text-sm text-slate-700">Snigdha & Associates</p>
                <p className="text-xs text-slate-400">Chartered Accountants</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Terms</Link>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Privacy</Link>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-100">
                <Shield className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs text-emerald-700">ISO 27001</span>
              </div>
              <p className="text-xs text-slate-400">© 2026 All rights reserved</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
